import React from 'react';
import _, { get, uniqBy, some, find, includes, filter } from 'lodash';
import PropTypes from 'prop-types';
import { Link, Route } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionGroup,
  AccordionHeader,
  AccordionHeaderSecondary,
  Checkbox,
  Radio,
  ButtonIcon,
  ButtonText,
  SecondaryButton
} from 'core/controls';
import {
  Translate,
  TranslateHTML,
  withTranslate,
  TranslateContext
} from 'core/translate';
import { SvgIcon } from 'core/icons';
import ScrollTop from '../../common/ScrollTop';
import DriveTypeToggle from './DriveTypeToggle';
import PriceWithDisclaimer from './PriceWithDisclaimer';
import OptionsAccordionHeader from '../../common/options/OptionsAccordionHeader';
import Raw from '../../common/Raw';
import TrimFeaturesModal from '../features/TrimFeaturesModal';
import EngineTransmissionDetailModal from './EngineTransmissionDetailModal';
import { findClosestMsrp } from './findClosestMsrp';
import { withModelInfo } from '../../common/ModelInfoContext';
import { FamilyFirstIneligibleTrimMessage } from '../../components/mathBox/FamilyFirstIneligibleMessage';

class TrimOption extends React.Component {
  onChange = () => {
    const { option, checked, onChange } = this.props;
    if (!checked) {
      onChange(option);
    }
  };

  render() {
    const {
      option,
      checked,
      analyticsKey,
      enableTrimFilterGrouping
    } = this.props;
    const description = option.trimName;

    return (
      <React.Fragment>
        <Checkbox checked={checked} onChange={this.onChange}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div className="option__title option__title--tf">
                {description}
              </div>
              {!enableTrimFilterGrouping && (
                <Link
                  to={`/trim/features/${option.styleId}`}
                  className="stat-image-link"
                  data-dtm={analyticsKey}
                  data-dtm2={description}>
                  <SvgIcon name="info" />
                </Link>
              )}
            </div>
          </div>
        </Checkbox>
        <FamilyFirstIneligibleTrimMessage
          eligibleForInvoicePrice={option.eligibleForInvoicePrice}
        />
      </React.Fragment>
    );
  }
}

class EngineTransmissionOption extends React.Component {
  onChange = () => {
    const { option, onChange } = this.props;
    onChange(option);
  };

  render() {
    const {
      option,
      checked,
      disclaimer,
      enableTrimFilterGrouping
    } = this.props;
    const {
      trimName,
      engine,
      transmission,
      engineDescription,
      transmissionDescription,
      msrp,
      message
    } = option;
    return (
      <Radio
        className="control--top option__control engine-transmission-option"
        checked={checked}
        onChange={this.onChange}>
        <div style={{ flex: 1 }}>
          <div>
            <Raw as="div" value={engineDescription} />
            {engineDescription !== transmissionDescription && (
              <Raw
                as="div"
                value={transmissionDescription}
                className="engine-transmission-option__transmission"
              />
            )}
          </div>
          <TranslateContext.Consumer>
            {translate => (
              <div className="option__disclaimer">
                <PriceWithDisclaimer
                  value={msrp}
                  message={message}
                  disclaimer={disclaimer}
                  analyticsKey={translate('component.common.select.engine')}
                  analyticsKey2={engineDescription}
                />
              </div>
            )}
          </TranslateContext.Consumer>
        </div>
        <div style={{ alignSelf: 'flex-end' }}>
          {enableTrimFilterGrouping ? (
            <Link
              to={`/trim/features/${option.styleId}`}
              className="stat-image-link">
              <SvgIcon name="info" />
            </Link>
          ) : (
            <Link
              to={`/trim/engine/${trimName}/${engine}/${transmission}`}
              className="stat-image-link">
              <SvgIcon name="info" />
            </Link>
          )}
        </div>
      </Radio>
    );
  }
}

const TrimGroup = React.forwardRef((props, ref) => {
  const {
    trimName,
    options,
    selections,
    disclaimer,
    msrp,
    enableTrimFilterGrouping,
    onChange
  } = props;
  const option =
    find(options, {
      engine: selections.engine,
      transmission: selections.transmission
    }) || findClosestMsrp(options, msrp);

  const isTrimSelected = some(options, { styleId: selections.styleId });

  return (
    <div
      className="option option--tf option--tf-trims"
      ref={isTrimSelected ? ref : null}>
      <TrimOption
        description={trimName}
        option={option}
        checked={isTrimSelected}
        enableTrimFilterGrouping={enableTrimFilterGrouping}
        onChange={onChange}
      />
      <div className="trim__engine-transmission-list">
        {options.map((option, index) => (
          <EngineTransmissionOption
            key={index}
            option={option}
            checked={
              isTrimSelected &&
              _.startsWith(option.engine, selections.engine) &&
              _.startsWith(option.transmission, selections.transmission)
            }
            disclaimer={disclaimer}
            enableTrimFilterGrouping={enableTrimFilterGrouping}
            onChange={onChange}
          />
        ))}
      </div>
    </div>
  );
});

TrimGroup.propTypes = {
  trimName: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  selections: PropTypes.object.isRequired,
  disclaimer: PropTypes.string,
  analyticsKey: PropTypes.string,
  onChange: PropTypes.func.isRequired
};

const NormalizeAndFilterByConfiguration = props => {
  const { children, matrix, styles, selections, filter } = props;

  const uniqueOptions = uniqBy(
    matrix.options,
    x =>
      `${x.bodyType}${x.driveType}${x.rearWheelType}${x.trimName}${x.engine}${
        x.transmission
      }`
  );

  const normalizeOptions = uniqueOptions.map(option => {
    const bodyType = find(matrix.bodyTypes, {
      id: option.bodyType
    });
    const engine = find(matrix.engines, {
      id: option.engine
    });
    const transmission = find(matrix.transmissions, {
      id: option.transmission
    });
    const styleByTrimName = find(styles, { trimName: option.trimName });
    return {
      ...option,
      cabSize: get(bodyType, 'cabSize', ''),
      rearWheelType: get(bodyType, 'rearWheelType', ''),
      engineDescription: get(engine, 'description', ''),
      engineLongCFD: get(engine, 'longCfd', ''),
      engineLargeImageUrl: get(engine, 'largeImageUrl', ''),
      engineDisclaimer: get(engine, 'disclaimer', ''),
      transmissionDescription: get(transmission, 'description', ''),
      transmissionLongCFD: get(transmission, 'longCfd', ''),
      transmissionLargeImageUrl: get(transmission, 'largeImageUrl', ''),
      transmissionDisclaimer: get(transmission, 'disclaimer', ''),
      eligibleForInvoicePrice: get(
        styleByTrimName,
        'eligibleForInvoicePrice',
        true
      )
    };
  });

  const hasCabSizes = some(matrix.bodyTypes, 'cabSize');

  const filteredOptions = filter
    ? normalizeOptions.filter(
        option =>
          hasCabSizes
            ? option.cabSize === selections.cabSize
            : option.bodyType === selections.bodyType
      )
    : normalizeOptions;

  return children(filteredOptions);
};

const toUniqueTrims = options =>
  uniqBy(options, 'trimName').map(x => x.trimName);

const toTrimGroups = (trims, options) =>
  trims.map(trimName => {
    return {
      trimName,
      options: options.filter(option => option.trimName === trimName)
    };
  });

const selectionHasChanged = (prevProps, props) => {
  return prevProps.selections.trimName !== props.selections.trimName;
};

const wasNotPreviouslyInList = (prevProps, props) => {
  return !some(
    prevProps.trimGroups,
    x => x.trimName === props.selections.trimName
  );
};

class AvailableTrimGroups extends React.Component {
  constructor(props) {
    super(props);
    this.ref = React.createRef();
  }

  componentDidUpdate(prevProps) {
    if (
      selectionHasChanged(prevProps, this.props) &&
      wasNotPreviouslyInList(prevProps, this.props)
    ) {
      const scrollOptionsSupported =
        'scrollBehavior' in document.documentElement.style;

      try {
        this.ref.current.scrollIntoView(
          scrollOptionsSupported
            ? {
                block: 'center',
                inline: 'nearest',
                behavior: 'smooth'
              }
            : false
        );
      } catch (e) {
        // in case scrollBehavior exists but options are not supported we do nothing
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView#Browser_compatibility
      }
    }
  }

  render() {
    const {
      trimGroups,
      selections,
      disclaimer,
      analyticsKey,
      msrp,
      enableTrimFilterGrouping,
      onChange
    } = this.props;

    return (
      <div>
        {trimGroups.map(x => {
          return (
            <TrimGroup
              key={x.trimName}
              trimName={x.trimName}
              options={x.options}
              selections={selections}
              disclaimer={disclaimer}
              analyticsKey={analyticsKey}
              msrp={msrp}
              enableTrimFilterGrouping={enableTrimFilterGrouping}
              ref={this.ref}
              onChange={onChange}
            />
          );
        })}
      </div>
    );
  }
}

const AvailableBodyType = ({ matrix, selections, filter }) => {
  if (!filter) {
    return null;
  }
  const bodyType = get(
    find(matrix.bodyTypes, {
      id: selections.bodyType
    }),
    'description'
  );

  return (
    <div className="trim__choices-available-body-type">
      <TranslateHTML
        value="component.common.choices.available.body.type"
        bodyType={bodyType}
      />
    </div>
  );
};

class TrimWithFilteringInner extends React.Component {
  onDriveChange = ({ driveType, rearWheelType }) => {
    const { options, config, onChange } = this.props;
    const { trimName, engine, transmission } = config.selections;
    const msrp = config.msrp.totalMsrpWithOptions;

    const filteredOptions = filter(options, { driveType, rearWheelType });

    const option =
      find(filteredOptions, { trimName, engine, transmission }) ||
      findClosestMsrp(filter(filteredOptions, { trimName })) ||
      findClosestMsrp(filteredOptions, msrp);

    onChange(option);
  };

  render() {
    const {
      translate,
      modelInfo,
      options,
      config,
      disclaimer,
      appResources,
      breakpoint,
      disableCompare,
      onChange,
      filterByConfig,
      matrix,
      enableTrimFilterGrouping
    } = this.props;
    const { selections, styles, msrp } = config;

    const driveAndWheelTypes = options.map(x => {
      const { driveType, rearWheelType } = x;
      return {
        id: `${driveType}-${rearWheelType}`,
        driveType,
        rearWheelType
      };
    });
    const uniqueDriveAndWheelTypes = uniqBy(driveAndWheelTypes, 'id');

    const allTrims = toUniqueTrims(options);

    const filteredOptionsByDriveType = options.filter(
      x =>
        x.driveType === selections.driveType &&
        x.rearWheelType === selections.rearWheelType
    );
    const uniqueTrims = toUniqueTrims(filteredOptionsByDriveType);
    const trimGroups = toTrimGroups(uniqueTrims, filteredOptionsByDriveType);

    const uniqueOptions = uniqBy(
      options,
      x => `${x.trimName}${x.engine}${x.transmission}`
    );
    const optionsNotMatchingDrive = uniqueOptions.filter(
      option => !includes(uniqueTrims, option.trimName)
    );
    const trimsNotMatching = toUniqueTrims(optionsNotMatchingDrive);
    const trimGroupsRequiringDriveChange = toTrimGroups(
      trimsNotMatching,
      optionsNotMatchingDrive
    );

    const driveTypeHeader = translate('component.common.select.drive.type');
    const trimsHeader = translate(
      allTrims.length === 1
        ? 'component.common.select.single.trim'
        : 'component.common.select.trim'
    );

    const collapseDriveTypes =
      modelInfo.enableTrimFilterCollapseSingleDrive &&
      uniqueDriveAndWheelTypes.length === 1;

    return (
      <React.Fragment>
        <AvailableBodyType
          matrix={matrix}
          selections={selections}
          filter={filterByConfig}
        />
        <Accordion>
          <AccordionGroup
            isOpenDefault={!collapseDriveTypes}
            id="configuration-drive-types">
            <AccordionHeader analyticsKey={driveTypeHeader}>
              {driveTypeHeader}
            </AccordionHeader>
            <AccordionContent>
              <DriveTypeToggle
                options={uniqueDriveAndWheelTypes}
                selections={selections}
                onChange={this.onDriveChange}
              />
            </AccordionContent>
          </AccordionGroup>
          <AccordionGroup>
            <OptionsAccordionHeader
              header={trimsHeader}
              count={allTrims.length}
              hideSingleCount={true}
            />
            <AccordionContent>
              {!disableCompare &&
                styles.length > 1 && (
                  <div className="trim__compare-link-container trim__compare-link-container--border-bottom">
                    <SecondaryButton
                      as={Link}
                      analytics="text"
                      to={`/trim/compare?styleOne=${selections.styleId}`}
                      className="button--inline-flex trim__compare-button"
                      style={{ minWidth: '50%' }}
                      data-dtm={translate('component.common.select.trim')}
                      id="trim-compare">
                      <ButtonText>
                        <Translate value="component.trim.compare.trims" />
                      </ButtonText>
                      <ButtonIcon
                        name="chevron-right"
                        className="svg-icon--small trim__chevron-icon--hide"
                      />
                    </SecondaryButton>
                  </div>
                )}
              <AvailableTrimGroups
                trimGroups={trimGroups}
                selections={selections}
                disclaimer={disclaimer}
                analyticsKey={trimsHeader}
                msrp={msrp.totalMsrpWithOptions}
                enableTrimFilterGrouping={enableTrimFilterGrouping}
                onChange={onChange}
              />
              {trimGroupsRequiringDriveChange.length > 0 && (
                <React.Fragment>
                  <AccordionHeaderSecondary>
                    <Translate value="component.common.available.other.drive.types" />
                  </AccordionHeaderSecondary>
                  <div>
                    {trimGroupsRequiringDriveChange.map(x => {
                      return (
                        <TrimGroup
                          key={x.trimName}
                          trimName={x.trimName}
                          options={x.options}
                          selections={selections}
                          disclaimer={disclaimer}
                          analyticsKey={trimsHeader}
                          msrp={msrp.totalMsrpWithOptions}
                          enableTrimFilterGrouping={enableTrimFilterGrouping}
                          onChange={onChange}
                        />
                      );
                    })}
                  </div>
                </React.Fragment>
              )}
            </AccordionContent>
          </AccordionGroup>
        </Accordion>
        <Route
          path="/trim/features/:styleId"
          render={({ match, history }) => {
            const styleId = match.params.styleId;
            const option = _.find(options, { styleId });
            const style =
              _.find(styles, { id: styleId }) ||
              _.find(styles, { trimName: option.trimName });

            if (!style) {
              return null;
            }

            return (
              <TrimFeaturesModal
                style={style}
                appResources={appResources}
                breakpoint={breakpoint}
                disclaimer={disclaimer}
                selections={selections}
                disableCompare={disableCompare}
                onClose={() => {
                  history.push('/trim');
                }}
                onSelect={() => {
                  history.push('/trim');
                  onChange(option);
                }}
              />
            );
          }}
        />
        <Route
          path="/trim/engine/:trimName/:engine/:transmission"
          render={({ match, history }) => {
            const { trimName, engine, transmission } = match.params;

            const option = find(
              filteredOptionsByDriveType.concat(optionsNotMatchingDrive),
              {
                trimName,
                engine,
                transmission
              }
            );

            if (!option) {
              history.push('/trim');
              return null;
            }

            return (
              <EngineTransmissionDetailModal
                option={option}
                disclaimer={disclaimer}
                selections={selections}
                onClose={() => {
                  history.push('/trim');
                }}
                onSelect={() => {
                  history.push('/trim');
                  onChange(option);
                }}
              />
            );
          }}
        />
      </React.Fragment>
    );
  }
}

const TrimWithFiltering = props => {
  const { matrix, config, filterByConfig, onChange } = props;

  return (
    <ScrollTop>
      <NormalizeAndFilterByConfiguration
        filter={filterByConfig}
        matrix={matrix}
        styles={config.styles}
        selections={config.selections}>
        {options => (
          <TrimWithFilteringInner
            {...props}
            options={options}
            onChange={onChange}
          />
        )}
      </NormalizeAndFilterByConfiguration>
    </ScrollTop>
  );
};

TrimWithFiltering.defaultProps = {
  enableTrimFilterGrouping: false
};

TrimWithFiltering.propTypes = {
  matrix: PropTypes.object.isRequired,
  config: PropTypes.object.isRequired,
  disclaimer: PropTypes.string,
  appResources: PropTypes.object.isRequired,
  breakpoint: PropTypes.string,
  disableCompare: PropTypes.bool,
  enableTrimFilterGrouping: PropTypes.bool,
  onChange: PropTypes.func.isRequired
};

export default withModelInfo(withTranslate(TrimWithFiltering));
